import { Action, Priority, useRegisterActions } from "kbar";
import { useCallback, useMemo } from "react";

import { EltSyncsQuery, useEltSyncsLazyQuery } from "@/apollo/types";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import cronExpressionToDayjs from "@/helpers/cronExpressionToDayjs";
import { getCronInfo } from "@/hooks/useCron";
import { IntegrationLogo, useIntegrationsMap } from "@/integrations";
import { formatRelativeTime } from "@/shared/formatters";

import { useViewDataSourceSlideOver } from "../../view-data-source-slideover";

type EltSync = NonNullable<EltSyncsQuery["eltSyncs"]>[number];

const formatSubtitle = (sync: EltSync) => {
  const cronExpr =
    (sync.orchestrationWorkflow
      ? sync.orchestrationWorkflow.cronExpression
      : sync.syncInterval) || undefined;

  if (!cronExpr) {
    return "No schedule set";
  }

  const cronInfo = getCronInfo(cronExpr).info;
  return `${cronInfo} - ${formatRelativeTime(cronExpressionToDayjs(cronExpr).toDate())}`;
};

export function useDataSourceSyncActions() {
  const [fetchSyncs, { data, loading }] = useEltSyncsLazyQuery();

  const { onOpen: viewDataSource } = useViewDataSourceSlideOver();

  const integrationsMap = useIntegrationsMap();

  const actions: Action[] = useMemo(() => {
    const actions: Action[] = [];

    if (loading) {
      actions.push({
        id: "loading-data-source-syncs",
        name: "Loading ...",
        priority: Priority.HIGH,
        section: "Data Source Syncs",
        icon: <LoadingSpinner id="loading" className="size-4" />,
        perform: () => {},
      });
    }

    data?.eltSyncs.forEach((sync) => {
      actions.push({
        id: sync.id,
        name: sync.destinationSchemaName || "Unnamed Sync",
        subtitle: formatSubtitle(sync),
        keywords: [
          sync.sourceIntegrationId,
          integrationsMap.get(sync.sourceIntegrationId)?.name.toLowerCase(),
        ]
          .filter(Boolean)
          .join(" "),
        priority: Priority.NORMAL,
        section: "Data Source Syncs",
        icon: (
          <IntegrationLogo id={sync.sourceIntegrationId} className="size-4" />
        ),
        perform: () => {
          viewDataSource({ syncId: sync.id });
        },
      });
    });

    return actions;
  }, [data, loading, viewDataSource, integrationsMap]);

  useRegisterActions(actions, [data?.eltSyncs, loading]);

  const register = useCallback(async () => {
    await fetchSyncs();
  }, [fetchSyncs]);

  return register;
}
